import { default as ReactEcharts } from "echarts-for-react";
import { t } from "i18next";
import { DateTime } from "luxon";
import ReactDomServer from "react-dom/server";
import { rgbColor } from "../../../../../enums/colors";
import _ from "lodash";
import { Event, EventsCalculation, EventsResponseItem, FShift, UsageDataset } from "../../../../../types/types";
import { SummaryUsageRow } from "../../../../../stores/TabsStore";


// 0: "equipment_item_id"
// 1: "event_date"
// 2: "shift_date"
// 3: "start"
// 4: "start_ts"
// 5: "finish"
// 6: "finish_ts"
// 7: "duration"
// 8: "state"
// 9: "equipment_item_status"
// 10: "personnel_id"
// 11: "equipment_item_division_id"
// 12: "personnel_division_id"
// 13: "state_changed"
// 14: "status_changed"
// 15: "person_changed"


interface GrpahData {
  name: string;
  type: string;
  data: number[];
  color: string;
  markLine?: any;
}

interface ChartData {
  totalWorked: number;
  totalWorkedPercent: number;
  totalStopped: number;
  totalTurnedOff: number;
  totalBroken: number;
  totalRepair: number;
  totalMaintenance: number;
  totalOther: number;
  chartData: {
    days: string[],
    worked: number[],
    stopped: number[],
    broken: number[],
    repair: number[],
    maintenance: number[],
    turned_off: number[],
    other: number[]
    workedPercents: number[],
    stoppedPercents: number[],
    repairPercents: number[],
    brokenPercents: number[],
    maintenancePercents: number[], 
    otherPercents: number[],
    turned_offPercents: number[]
  }
}

export const LoadBarChart = ({data}:{data: SummaryUsageRow}) => {
  let totalWorkedPercent = data.availability.worked.percent * 100
  let days = data.data.map(e => e.dateShift.start_day.toSQLDate())
  let workedPercents = data.data.map(e => e.availability.worked.percent * 100)
  let stoppedPercents = data.data.map(e => e.availability.stopped.percent * 100)
  let brokenPercents = data.data.map(e => e.broken.total.percent * 100)
  let repairPercents = data.data.map(e => e.repair.total.percent * 100)
  let servicePercents = data.data.map(e => e.service.total.percent * 100)
  let otherPercents = data.data.map(e => e.other.total.percent * 100)
  let turnedOffPercents = data.data.map(e => (e.unavailability.off.percent + e.availability.off.percent) * 100)

  const graphData: GrpahData[] = [
    {
      name: "worked",
      type: "bar",
      data: workedPercents!,
      color: rgbColor.worked,
      markLine: {
        data: [
          [
            {
              name: "",
              coord: [0, totalWorkedPercent!],
            },
            {
              coord: [days.length! - 1, totalWorkedPercent!],
            },
          ],
        ],
        silent: true,
      },
    },
    { name: "stopped", type: "bar", data: stoppedPercents!, color: rgbColor.stopped },
    { name: "broken", type: "bar", data: brokenPercents!, color: rgbColor.broken },
    { name: "repair", type: "bar", data: repairPercents!, color: rgbColor.repair },
    { name: "service", type: "bar", data: servicePercents!, color: rgbColor.service },
    { name: "other", type: "bar", data: otherPercents!, color: rgbColor.other },
    { name: "turned_off", type: "bar", data: turnedOffPercents!, color: rgbColor.turned_off},
  ];

  const getOptions = () => ({
    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "shadow",
      },
      formatter: function (params: {name: any; seriesName: string; value: (number | string | undefined)[] }[]) {
        return ReactDomServer.renderToStaticMarkup(<Tooltip params={params} />);
      }
    },
    legend: {
      icon: "circle",
      bottom: 0,
      formatter: function (name: string) {
        return t(name);
      }
    },
    grid: {
      left: "0",
      right: "0%",
      bottom: "30px",
      top: "10px",
      containLabel: true,
    },
    xAxis: [
      {
        type: "category",
        data: days,
        axisLabel: {
          formatter: function (value: string) {
            return DateTime.fromSQL(value).toFormat("dd");
          },
        },
      },
    ],
    yAxis: [
      {
        type: "value",
        splitLine: {
          lineStyle: {
            color: "rgba(128,128,128,0.1)",
          },
        },
        position: "left",
        axisLabel: {
          max: 100,
          formatter: function (value: string) {
            return `${value} %`;
          },
        },
      },
    ],
    series: graphData,
  });

  return (
    <ReactEcharts option={getOptions()} style={{ height: 200, width: "100%" }} opts={{ renderer: "canvas" }} />
  )
}

const Tooltip = ({ params }: { params: { name: any; seriesName: string; value: (number | string | undefined)[] }[] }) => {
  let date = DateTime.fromSQL(params[0].name!)
  return (
    <div className="relative bg-background">
      <div className="font-bold uppercase pb-3">{date.weekdayShort} {date.toFormat("dd.MM.yyyy")}</div>
      <div className="flex flex-col gap-1">
        {params.map(item => (
          <div key={item.name} className="flex w-48 justify-between ">
            <div className="capitalize">{t(`${item.seriesName}`)}</div>
            <div className="font-bold">{Number(item.value).toFixed(2)}%</div>
          </div>
        ))}
      </div>

    </div>
  );
};